import type { Feature, Polygon } from 'geojson';
import type { Coordinate } from 'ol/coordinate';

// ============================================================================
// Equal-area core placement kernel (capacity-constrained Lloyd, grid-based).
//
// Fully automatic: places `k` cores so each owns ~equal area while their cell
// centers of mass spread as far apart as the geometry allows, then relocates
// each core toward its cell's pole of inaccessibility. An anisotropy-gated
// inter-site spread force staggers cores off the centerline of thin bands
// (where pure Lloyd collapses to a straight line). Deterministic; no RNG.
// Pure domain service — no I/O, no turf in the hot loop (planar meter math).
// ============================================================================

const METERS_PER_DEG_LAT = 111_320;

/** Baked tuning — production ships no user knobs; the dev tuning panel overrides via options. */
const TARGET_SAMPLES = 3000; // grid-resolution target (interior raster cells)
const MAX_SAMPLES = 8000; // hard cap so very large zones stay fast
const MIN_STEP_M = 0.5; // finest grid spacing
const MAX_STEP_M = 5; // coarsest grid spacing
const CAP_INNER_ITERS = 4; // equal-area weight sub-iterations per Lloyd step
const CAP_RATE = 0.5; // weight-update gain
const CAP_CLAMP = 3; // |w_i| <= CAP_CLAMP * cellArea
const MAX_ITERATIONS = 30;
const CONVERGENCE_M = 0.5;
/** Final core = anchor + (pole - anchor) * CENTER_BLEND. 0 = anchor, 1 = pole. */
const CENTER_BLEND = 0.5;
/**
 * Inter-site repulsion gain. Pure Lloyd collapses thin bands to a centerline
 * (slab cells: CoM and pole both sit on it); the spread force pushes each site
 * away from its neighbors so a staggered pattern EMERGES instead. 0 disables.
 * The effective gain is modulated per cell by {@link anisotropyGain} — only
 * elongated cells spread; isotropic layouts (square → quadrants) are untouched.
 */
const SPREAD_RATE = 1;
/** Repulsion d² floor (× cellArea) — keeps the force finite for near-coincident sites. */
const SPREAD_D2_FLOOR = 0.05;
/** Max spread offset per iteration (× √cellArea). */
const SPREAD_MAX_STEP = 0.7;
/** Cells with aspect ratio below this are isotropic — no spread force. */
const SPREAD_ASPECT_DEADBAND = 1.2;

export interface EqualAreaPlacerOptions {
  targetSamples?: number;
  maxSamples?: number;
  minStepMeters?: number;
  maxStepMeters?: number;
  capInnerIters?: number;
  capRate?: number;
  capClamp?: number;
  maxIterations?: number;
  convergenceMeters?: number;
  /** 0 = pure cell anchor (CoM, or site when spreadRate > 0), 1 = pure pole of inaccessibility. */
  centerBlend?: number;
  /** Inter-site repulsion gain; 0 disables (pure capacity-Lloyd). */
  spreadRate?: number;
}

/** Production defaults — also seed the dev tuning panel's initial values. */
export const EQUAL_AREA_DEFAULTS: Required<EqualAreaPlacerOptions> = {
  targetSamples: TARGET_SAMPLES,
  maxSamples: MAX_SAMPLES,
  minStepMeters: MIN_STEP_M,
  maxStepMeters: MAX_STEP_M,
  capInnerIters: CAP_INNER_ITERS,
  capRate: CAP_RATE,
  capClamp: CAP_CLAMP,
  maxIterations: MAX_ITERATIONS,
  convergenceMeters: CONVERGENCE_M,
  centerBlend: CENTER_BLEND,
  spreadRate: SPREAD_RATE,
};

/** Result of {@link computeEqualAreaPlacement}: the placed cores plus the raster grid it used. */
export interface EqualAreaPlacement {
  /** The `k` placed cores (lon/lat). */
  cores: Coordinate[];
  /** Interior grid-sample centers (lon/lat), parallel to {@link EqualAreaPlacement.gridAssign}. */
  gridSamples: Coordinate[];
  /** Cell index (0..k-1) each grid sample is assigned to. */
  gridAssign: number[];
  /** Grid spacing in meters (square size for display). */
  stepMeters: number;
}

interface LocalFrame {
  toLocal: (c: Coordinate) => [number, number];
  toLonLat: (m: [number, number]) => Coordinate;
}

interface GridSample {
  x: number;
  y: number;
  gx: number;
  gy: number;
}

/** Local tangent-plane frame anchored at (lonRef, latRef); good to ~mm sub-km. */
function localFrameAt(lonRef: number, latRef: number): LocalFrame {
  const cosLat = Math.cos((latRef * Math.PI) / 180);
  const mPerDegLon = METERS_PER_DEG_LAT * cosLat;

  return {
    toLocal: (c) => [(c[0] - lonRef) * mPerDegLon, (c[1] - latRef) * METERS_PER_DEG_LAT],
    toLonLat: (m) => [lonRef + m[0] / mPerDegLon, latRef + m[1] / METERS_PER_DEG_LAT],
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Shoelace signed area of a ring (meter-space). Closing-duplicate vertex is harmless. */
function ringSignedArea(ring: [number, number][]): number {
  let a = 0;

  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % ring.length];
    a += x1 * y2 - x2 * y1;
  }

  return a / 2;
}

/** Ray-cast point-in-ring (meter-space). */
function pointInRing(ring: [number, number][], x: number, y: number): boolean {
  let inside = false;

  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];

    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }

  return inside;
}

/** Inside the outer ring AND outside every hole ring. */
function pointInRings(rings: [number, number][][], x: number, y: number): boolean {
  if (!pointInRing(rings[0], x, y)) {
    return false;
  }

  for (let h = 1; h < rings.length; h++) {
    if (pointInRing(rings[h], x, y)) {
      return false;
    }
  }

  return true;
}

/**
 * Power-diagram assignment — decides which cell (core) each grid sample belongs
 * to. The most-called function in the kernel and the core of area-equalization.
 *
 * Each sample is assigned to `argmin_i(d²_i − w_i)`: the cell `i` that minimizes
 * its squared distance to seed `i` MINUS that cell's weight `w_i`. (`argmin` is
 * the winning index, not the value.) This is a **power diagram** — a.k.a. a
 * Laguerre / additively-weighted Voronoi:
 *   - With every `w_i = 0` it reduces to a plain nearest-seed Voronoi.
 *   - Raising `w_i` lets cell `i` win samples that are slightly farther away, so
 *     the cell grows; lowering `w_i` shrinks it. The cell boundaries stay
 *     straight lines — they just slide. This is the lever the equal-area loop
 *     pulls: nudge `w_i` up for cells that own too few samples, down for too many.
 *
 * Writes the winning cell index into the pre-allocated `out[s]` instead of
 * returning a fresh array — it runs dozens of times per placement (CAP_INNER_ITERS
 * × MAX_ITERATIONS), so reusing the buffer avoids garbage-collector churn.
 */
function assignPower(samples: GridSample[], seeds: [number, number][], w: Float64Array, out: Int32Array): void {
  for (let s = 0; s < samples.length; s++) {
    const px = samples[s].x;
    const py = samples[s].y;
    let bi = 0; // best cell index so far (the argmin)
    let bv = Infinity; // smallest power distance (d² − w) seen so far

    for (let i = 0; i < seeds.length; i++) {
      const dx = px - seeds[i][0];
      const dy = py - seeds[i][1];
      const v = dx * dx + dy * dy - w[i]; // power distance to cell i: d²_i − w_i

      if (v < bv) {
        bv = v;
        bi = i;
      }
    }

    out[s] = bi; // sample s belongs to cell bi
  }
}

/**
 * Spread gain from cell elongation, given the cell's sample covariance
 * (cxx, cyy, cxy): 0 for ~isotropic cells (aspect ≤ {@link SPREAD_ASPECT_DEADBAND}),
 * ramping up to 1 as the cell gets thinner. Thin slab cells are exactly the
 * failure mode the spread force exists for — isotropic layouts (e.g. square →
 * quadrants) are already well spread and must not be disturbed.
 */
function anisotropyGain(cxx: number, cyy: number, cxy: number): number {
  // Eigenvalues of the 2×2 covariance matrix; aspect = √(λmax/λmin).
  const t = (cxx + cyy) / 2;
  const det = cxx * cyy - cxy * cxy;
  const s = Math.sqrt(Math.max(t * t - det, 0));
  const lMin = Math.max(t - s, 1e-9);
  const aspect = Math.sqrt((t + s) / lMin);

  return clamp(aspect - SPREAD_ASPECT_DEADBAND, 0, 1);
}

function counts(assign: Int32Array, k: number): Int32Array {
  const c = new Int32Array(k);

  for (let s = 0; s < assign.length; s++) {
    c[assign[s]]++;
  }

  return c;
}

/**
 * Capacity balancing: `iters` rounds of reassign + proportional weight update
 * (cells short on samples grow, oversized ones shrink), clamped and
 * mean-subtracted. Seeds are NOT moved — weights only.
 */
function balanceWeights(
  samples: GridSample[],
  seeds: [number, number][],
  w: Float64Array,
  assign: Int32Array,
  target: number,
  cellArea: number,
  capRate: number,
  capClamp: number,
  iters: number,
): void {
  const k = seeds.length;

  for (let t = 0; t < iters; t++) {
    assignPower(samples, seeds, w, assign);
    const cnt = counts(assign, k);
    let mean = 0;

    for (let i = 0; i < k; i++) {
      w[i] += capRate * ((target - cnt[i]) / target) * cellArea;
      w[i] = clamp(w[i], -capClamp * cellArea, capClamp * cellArea);
      mean += w[i];
    }

    mean /= k;

    for (let i = 0; i < k; i++) {
      w[i] -= mean;
    }
  }
}

/** Deterministic farthest-point seeding (k-means++ without the randomness). */
function farthestPointSeeds(samples: GridSample[], k: number): [number, number][] {
  let mx = 0;
  let my = 0;

  for (const s of samples) {
    mx += s.x;
    my += s.y;
  }

  mx /= samples.length;
  my /= samples.length;

  let startIdx = 0;
  let startBest = Infinity;

  for (let s = 0; s < samples.length; s++) {
    const dx = samples[s].x - mx;
    const dy = samples[s].y - my;
    const d = dx * dx + dy * dy;

    if (d < startBest) {
      startBest = d;
      startIdx = s;
    }
  }

  const seeds: [number, number][] = [[samples[startIdx].x, samples[startIdx].y]];
  const nearest = new Float64Array(samples.length);

  for (let s = 0; s < samples.length; s++) {
    const dx = samples[s].x - seeds[0][0];
    const dy = samples[s].y - seeds[0][1];
    nearest[s] = dx * dx + dy * dy;
  }

  while (seeds.length < k) {
    let far = 0;
    let farBest = -1;

    for (let s = 0; s < samples.length; s++) {
      if (nearest[s] > farBest) {
        farBest = nearest[s];
        far = s;
      }
    }

    const ns: [number, number] = [samples[far].x, samples[far].y];
    seeds.push(ns);

    for (let s = 0; s < samples.length; s++) {
      const dx = samples[s].x - ns[0];
      const dy = samples[s].y - ns[1];
      const d = dx * dx + dy * dy;

      if (d < nearest[s]) {
        nearest[s] = d;
      }
    }
  }

  return seeds;
}

/** The sample in the largest cell farthest from its own seed — for reseeding empties. */
function reseedFromLargest(
  samples: GridSample[],
  assign: Int32Array,
  seeds: [number, number][],
  cnt: Int32Array,
): [number, number] {
  let big = 0;

  for (let i = 1; i < cnt.length; i++) {
    if (cnt[i] > cnt[big]) {
      big = i;
    }
  }

  let best = -1;
  let bestS = samples[0];

  for (let s = 0; s < samples.length; s++) {
    if (assign[s] !== big) {
      continue;
    }

    const dx = samples[s].x - seeds[big][0];
    const dy = samples[s].y - seeds[big][1];
    const d = dx * dx + dy * dy;

    if (d > best) {
      best = d;
      bestS = samples[s];
    }
  }

  return [bestS.x, bestS.y];
}

/** Nearest sample to a point — guarantees an inside fallback. */
function nearestSample(samples: GridSample[], x: number, y: number): GridSample {
  let best = Infinity;
  let bestS = samples[0];

  for (const s of samples) {
    const dx = s.x - x;
    const dy = s.y - y;
    const d = dx * dx + dy * dy;

    if (d < best) {
      best = d;
      bestS = s;
    }
  }

  return bestS;
}

/** Per-cell pole of inaccessibility via a two-pass chamfer distance transform. */
function cellPoles(
  samples: GridSample[],
  assign: Int32Array,
  k: number,
  gcols: number,
  grows: number,
): Array<[number, number] | null> {
  const n = gcols * grows;
  const lab = new Int32Array(n).fill(-1);

  for (let s = 0; s < samples.length; s++) {
    lab[samples[s].gy * gcols + samples[s].gx] = assign[s];
  }

  const dist = new Float64Array(n).fill(Infinity);
  const L = (gx: number, gy: number) => (gx < 0 || gy < 0 || gx >= gcols || gy >= grows ? -1 : lab[gy * gcols + gx]);

  for (let gy = 0; gy < grows; gy++) {
    for (let gx = 0; gx < gcols; gx++) {
      const l = lab[gy * gcols + gx];

      if (l < 0) {
        continue;
      }

      if (L(gx - 1, gy) !== l || L(gx + 1, gy) !== l || L(gx, gy - 1) !== l || L(gx, gy + 1) !== l) {
        dist[gy * gcols + gx] = 0;
      }
    }
  }

  const d1 = 1;
  const d2 = Math.SQRT2;

  const rel = (l: number, ngx: number, ngy: number, c: number, d: number) => {
    if (ngx < 0 || ngy < 0 || ngx >= gcols || ngy >= grows) {
      return d;
    }

    const nid = ngy * gcols + ngx;

    return lab[nid] !== l ? d : Math.min(d, dist[nid] + c);
  };

  for (let gy = 0; gy < grows; gy++) {
    for (let gx = 0; gx < gcols; gx++) {
      const id = gy * gcols + gx;
      const l = lab[id];

      if (l < 0) {
        continue;
      }

      let d = dist[id];
      d = rel(l, gx - 1, gy, d1, d);
      d = rel(l, gx, gy - 1, d1, d);
      d = rel(l, gx - 1, gy - 1, d2, d);
      d = rel(l, gx + 1, gy - 1, d2, d);
      dist[id] = d;
    }
  }

  for (let gy = grows - 1; gy >= 0; gy--) {
    for (let gx = gcols - 1; gx >= 0; gx--) {
      const id = gy * gcols + gx;
      const l = lab[id];

      if (l < 0) {
        continue;
      }

      let d = dist[id];
      d = rel(l, gx + 1, gy, d1, d);
      d = rel(l, gx, gy + 1, d1, d);
      d = rel(l, gx + 1, gy + 1, d2, d);
      d = rel(l, gx - 1, gy + 1, d2, d);
      dist[id] = d;
    }
  }

  const best = new Float64Array(k).fill(-1);
  const poles: Array<[number, number] | null> = new Array(k).fill(null);

  for (let s = 0; s < samples.length; s++) {
    const a = assign[s];
    const dv = dist[samples[s].gy * gcols + samples[s].gx];

    if (dv > best[a]) {
      best[a] = dv;
      poles[a] = [samples[s].x, samples[s].y];
    }
  }

  return poles;
}

/** Defensive fallback for polygons too small to grid `k` cells. */
function fallbackPlacement(rings: [number, number][][], k: number, frame: LocalFrame): Coordinate[] {
  const outer = rings[0];
  let cx = 0;
  let cy = 0;

  // Mean of distinct outer-ring vertices (drop closing duplicate).
  const last = outer.length - 1;
  const closed = outer[0][0] === outer[last][0] && outer[0][1] === outer[last][1];
  const count = closed ? outer.length - 1 : outer.length;

  for (let i = 0; i < count; i++) {
    cx += outer[i][0];
    cy += outer[i][1];
  }

  cx /= count;
  cy /= count;

  let minX = Infinity;
  let maxX = -Infinity;

  for (const [x] of outer) {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
  }

  const r = Math.max((maxX - minX) / 8, 0.5);
  const out: Coordinate[] = [];

  for (let i = 0; i < k; i++) {
    const angle = (i / Math.max(k, 1)) * 2 * Math.PI;
    let px = cx;
    let py = cy;

    for (let step = 0; step <= 8; step++) {
      const tx = cx + r * step * 0.25 * Math.cos(angle);
      const ty = cy + r * step * 0.25 * Math.sin(angle);

      if (pointInRings(rings, tx, ty)) {
        px = tx;
        py = ty;
        break;
      }
    }

    out.push(frame.toLonLat([px, py]));
  }

  return out;
}

/**
 * Place `k` cores inside `polygon` so each owns ~equal area (lon/lat). Thin
 * wrapper over {@link computeEqualAreaPlacement} that returns only the cores.
 */
export function placeCoresEqualArea(
  polygon: Feature<Polygon>,
  k: number,
  options?: EqualAreaPlacerOptions,
): Coordinate[] {
  return computeEqualAreaPlacement(polygon, k, options).cores;
}

/**
 * Place `k` cores inside `polygon` so each owns ~equal area while their cell
 * centers of mass spread as far apart as the geometry allows; each core is then
 * relocated toward its cell's pole of inaccessibility ({@link CENTER_BLEND}).
 * On elongated cells (thin bands) the spread force ({@link SPREAD_RATE}) pushes
 * sites off the centerline so a staggered pattern emerges instead of a line.
 *
 * Also returns the rasterized grid (samples + per-sample cell assignment) used
 * internally — for diagnostics / the Technician grid overlay. Handles holes
 * (a core never lands in an interior ring). Deterministic.
 */
export function computeEqualAreaPlacement(
  polygon: Feature<Polygon>,
  k: number,
  options?: EqualAreaPlacerOptions,
): EqualAreaPlacement {
  if (k <= 0) {
    return { cores: [], gridSamples: [], gridAssign: [], stepMeters: 0 };
  }

  const targetSamples = options?.targetSamples ?? TARGET_SAMPLES;
  const maxSamples = options?.maxSamples ?? MAX_SAMPLES;
  const minStep = options?.minStepMeters ?? MIN_STEP_M;
  const maxStep = options?.maxStepMeters ?? MAX_STEP_M;
  const capInnerIters = options?.capInnerIters ?? CAP_INNER_ITERS;
  const capRate = options?.capRate ?? CAP_RATE;
  const capClamp = options?.capClamp ?? CAP_CLAMP;
  const maxIterations = options?.maxIterations ?? MAX_ITERATIONS;
  const convergenceMeters = options?.convergenceMeters ?? CONVERGENCE_M;
  const centerBlend = options?.centerBlend ?? CENTER_BLEND;
  const spreadRate = options?.spreadRate ?? SPREAD_RATE;

  const lonLatRings = polygon.geometry.coordinates;

  if (lonLatRings.length === 0 || lonLatRings[0].length < 4) {
    return { cores: [], gridSamples: [], gridAssign: [], stepMeters: 0 };
  }

  // bbox (lon/lat) of the outer ring → frame anchor.
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;

  for (const [lon, lat] of lonLatRings[0]) {
    minLon = Math.min(minLon, lon);
    minLat = Math.min(minLat, lat);
    maxLon = Math.max(maxLon, lon);
    maxLat = Math.max(maxLat, lat);
  }

  const frame = localFrameAt((minLon + maxLon) / 2, (minLat + maxLat) / 2);
  const rings: [number, number][][] = lonLatRings.map((r) => r.map((c) => frame.toLocal(c as Coordinate)));

  // bbox + area in meters.
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const [x, y] of rings[0]) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }

  let areaM = Math.abs(ringSignedArea(rings[0]));

  for (let h = 1; h < rings.length; h++) {
    areaM -= Math.abs(ringSignedArea(rings[h]));
  }

  if (areaM <= 0) {
    return { cores: fallbackPlacement(rings, k, frame), gridSamples: [], gridAssign: [], stepMeters: 0 };
  }

  let step = clamp(Math.sqrt(areaM / targetSamples), minStep, maxStep);

  if (areaM / (step * step) > maxSamples) {
    step = Math.sqrt(areaM / maxSamples);
  }

  const gcols = Math.max(1, Math.floor((maxX - minX) / step) + 1);
  const grows = Math.max(1, Math.floor((maxY - minY) / step) + 1);
  const x0 = minX + step / 2;
  const y0 = minY + step / 2;

  const samples: GridSample[] = [];

  for (let gy = 0; gy < grows; gy++) {
    for (let gx = 0; gx < gcols; gx++) {
      const x = x0 + gx * step;
      const y = y0 + gy * step;

      if (pointInRings(rings, x, y)) {
        samples.push({ x, y, gx, gy });
      }
    }
  }

  if (samples.length < k) {
    return { cores: fallbackPlacement(rings, k, frame), gridSamples: [], gridAssign: [], stepMeters: step };
  }

  const totalArea = samples.length * step * step;
  const cellArea = totalArea / k;
  const target = samples.length / k;

  const seeds = farthestPointSeeds(samples, k);
  const w = new Float64Array(k);
  const assign = new Int32Array(samples.length);

  for (let iter = 0; iter < maxIterations; iter++) {
    // Equal-area capacity balancing.
    balanceWeights(samples, seeds, w, assign, target, cellArea, capRate, capClamp, capInnerIters);

    // Lloyd move → cell centroid + spread offset (inter-site repulsion).
    assignPower(samples, seeds, w, assign);
    const cnt = counts(assign, k);
    const sx = new Float64Array(k);
    const sy = new Float64Array(k);
    const sxx = new Float64Array(k);
    const syy = new Float64Array(k);
    const sxy = new Float64Array(k);

    for (let s = 0; s < samples.length; s++) {
      const a = assign[s];
      const { x, y } = samples[s];
      sx[a] += x;
      sy[a] += y;
      sxx[a] += x * x;
      syy[a] += y * y;
      sxy[a] += x * y;
    }

    const maxSpreadStep = SPREAD_MAX_STEP * Math.sqrt(cellArea);
    const d2Floor = SPREAD_D2_FLOOR * cellArea;
    // All repulsion forces read the PRE-move seed positions → order-independent.
    const next: Array<[number, number] | null> = new Array(k).fill(null);

    for (let i = 0; i < k; i++) {
      if (cnt[i] === 0) {
        continue;
      }

      const cx = sx[i] / cnt[i];
      const cy = sy[i] / cnt[i];
      let nx = cx;
      let ny = cy;
      // Effective gain = user gain × how elongated this cell is.
      const gain =
        spreadRate > 0 && k > 1
          ? spreadRate * anisotropyGain(sxx[i] / cnt[i] - cx * cx, syy[i] / cnt[i] - cy * cy, sxy[i] / cnt[i] - cx * cy)
          : 0;

      if (gain > 0) {
        // Sum of 1/d² pushes away from every other site; cellArea scaling makes
        // the equilibrium offset ∝ √cellArea (scale-free across zone sizes).
        let rx = 0;
        let ry = 0;

        for (let j = 0; j < k; j++) {
          if (j === i) {
            continue;
          }

          const ddx = seeds[i][0] - seeds[j][0];
          const ddy = seeds[i][1] - seeds[j][1];
          const d2 = Math.max(ddx * ddx + ddy * ddy, d2Floor);
          rx += ddx / d2;
          ry += ddy / d2;
        }

        let ox = gain * cellArea * rx;
        let oy = gain * cellArea * ry;
        const oLen = Math.hypot(ox, oy);

        if (oLen > maxSpreadStep) {
          ox *= maxSpreadStep / oLen;
          oy *= maxSpreadStep / oLen;
        }

        // Pushed outside the polygon → halve the offset until inside (smooth
        // approach to the boundary; a hard snap-back to the centroid would
        // oscillate and never converge). All halvings fail → pure centroid.
        for (let h = 0; h < 5; h++) {
          if (pointInRings(rings, cx + ox, cy + oy)) {
            nx = cx + ox;
            ny = cy + oy;
            break;
          }

          ox /= 2;
          oy /= 2;
        }
      }

      next[i] = [nx, ny];
    }

    let maxDisp = 0;

    for (let i = 0; i < k; i++) {
      const n = next[i];

      if (n === null) {
        seeds[i] = reseedFromLargest(samples, assign, seeds, cnt);
        w[i] = 0;
        continue;
      }

      const disp = Math.hypot(n[0] - seeds[i][0], n[1] - seeds[i][1]);

      if (disp > maxDisp) {
        maxDisp = disp;
      }

      seeds[i] = n;
    }

    if (maxDisp < convergenceMeters) {
      break;
    }
  }

  // Final capacity polish — the spread force can leave the loop mid-churn at
  // maxIterations; equal area is PRIMARY, so re-balance with the sites frozen.
  balanceWeights(samples, seeds, w, assign, target, cellArea, capRate, capClamp, capInnerIters * 2);

  // Final partition → anchor + pole per cell.
  assignPower(samples, seeds, w, assign);
  const finalCnt = counts(assign, k);
  const sx = new Float64Array(k);
  const sy = new Float64Array(k);

  for (let s = 0; s < samples.length; s++) {
    const a = assign[s];
    sx[a] += samples[s].x;
    sy[a] += samples[s].y;
  }

  const poles = cellPoles(samples, assign, k, gcols, grows);
  const out: Coordinate[] = [];

  for (let i = 0; i < k; i++) {
    if (finalCnt[i] === 0) {
      const s = nearestSample(samples, seeds[i][0], seeds[i][1]);
      out.push(frame.toLonLat([s.x, s.y]));
      continue;
    }

    const comX = sx[i] / finalCnt[i];
    const comY = sy[i] / finalCnt[i];
    // With the spread force on, the converged site CARRIES the lateral offset —
    // a slab cell's CoM (and pole) sit on the band centerline, so anchoring at
    // the CoM would erase the stagger the repulsion just built.
    const anchorX = spreadRate > 0 ? seeds[i][0] : comX;
    const anchorY = spreadRate > 0 ? seeds[i][1] : comY;
    const pole = poles[i] ?? [anchorX, anchorY];

    let px = anchorX + (pole[0] - anchorX) * centerBlend;
    let py = anchorY + (pole[1] - anchorY) * centerBlend;

    // Concave cell: blended point may escape. Pole is a sample → always inside.
    if (!pointInRings(rings, px, py)) {
      px = pole[0];
      py = pole[1];
    }

    out.push(frame.toLonLat([px, py]));
  }

  return {
    cores: out,
    gridSamples: samples.map((s) => frame.toLonLat([s.x, s.y])),
    gridAssign: Array.from(assign),
    stepMeters: step,
  };
}
